import { Alerta, Badge, Button, DataList, Dialog, MetricCard, Row, Spinner, Stack, type ColumnaDataList } from "@/components";
import type { Llamado } from "@/lib/api";
import { ETIQUETA_ESTADO_LLAMADO, TONO_ESTADO_LLAMADO } from "@/lib/aportes";
import { formatFecha, formatMoneda } from "@/lib/format";
import { parseMonto } from "@/lib/gastos";
import { FormularioLlamado } from "./FormularioLlamado";
import { useLlamadosTab } from "./useLlamadosTab";

export function LlamadosTab() {
  const t = useLlamadosTab();

  const columnas: ColumnaDataList<Llamado>[] = [
    { clave: "numero", titulo: "Previsión", render: (l) => `Previsión ${l.numero}` },
    { clave: "fecha", titulo: "Fecha", render: (l) => formatFecha(l.fecha) },
    { clave: "vencimiento", titulo: "Vence", render: (l) => formatFecha(l.vencimiento) },
    { clave: "descripcion", titulo: "Descripción", render: (l) => l.descripcion ?? "—" },
    { clave: "monto", titulo: "Pedido", render: (l) => formatMoneda(l.monto_usd, "USD") },
    { clave: "aportado", titulo: "Aportado", render: (l) => formatMoneda(l.aportado_usd, "USD") },
    {
      clave: "estado",
      titulo: "Pendiente",
      render: (l) => (
        <Row espacio={2}>
          <span>{formatMoneda(l.pendiente_usd, "USD")}</span>
          <Badge tono={TONO_ESTADO_LLAMADO[l.estado]}>{ETIQUETA_ESTADO_LLAMADO[l.estado]}</Badge>
        </Row>
      ),
    },
  ];

  const acciones = (l: Llamado) => (
    <Button variante="secundario" tamanio="sm" aria-label={`Ver previsión ${l.numero}`} onClick={() => t.verFicha(l.id)}>
      Ver
    </Button>
  );

  return (
    <Stack espacio={6}>
      <Row justificar="entre" envolver espacio={3}>
        <h1 className="text-pantalla font-light tracking-[-0.03em]">Previsiones de gastos</h1>
        {t.esAdmin && (
          <Row espacio={2} envolver>
            <Button variante="secundario" onClick={t.armarPrevision}>
              Armar previsión
            </Button>
            <Button onClick={t.abrirNuevo}>Nueva previsión</Button>
          </Row>
        )}
      </Row>

      {t.totales && (
        <div className="grid gap-3 sm:grid-cols-3">
          <MetricCard titulo="Pedido" valor={formatMoneda(t.totales.pedido_usd, "USD")} />
          <MetricCard titulo="Aportado" valor={formatMoneda(t.totales.aportado_usd, "USD")} />
          <MetricCard
            titulo="Pendiente"
            valor={formatMoneda(t.totales.pendiente_usd, "USD")}
            detalle={
              parseMonto(t.totales.vencido_usd) !== null ? (
                <span className="text-peligro">Vencido: {formatMoneda(t.totales.vencido_usd, "USD")}</span>
              ) : undefined
            }
          />
        </div>
      )}

      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.errorCarga && (
        <Stack espacio={3}>
          <Alerta texto={t.errorCarga} />
          <div>
            <Button variante="secundario" onClick={t.reintentar}>
              Reintentar
            </Button>
          </div>
        </Stack>
      )}
      {!t.cargando && !t.errorCarga && (
        <DataList etiqueta="Previsiones de gastos" columnas={columnas} filas={t.items} claveFila={(l) => l.id} acciones={acciones} vacio="Todavía no hay previsiones de gastos." />
      )}

      <Dialog abierto={t.abierto} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Nueva previsión">
        <FormularioLlamado
          form={t.form}
          cambiar={t.cambiar}
          errores={t.errores}
          vistaPrevia={t.vistaPrevia}
          errorVistaPrevia={t.errorVistaPrevia}
          propuesta={t.propuesta}
          onSubmit={(e) => void t.guardar(e)}
          onCancelar={t.cerrar}
          guardando={t.guardando}
          error={t.errorGuardar}
          textoGuardar="Emitir previsión"
        />
      </Dialog>
    </Stack>
  );
}
